import type { MaybeRef, Ref, UnwrapNestedRefs } from 'vue';
import type { WritableDeep } from 'type-fest';
import { klona } from 'klona/json';

// worth to read articles
// • https://logaretm.com/blog/juggling-refs-around/
// • https://antfu.me/posts/composable-vue-vueday-2021

export const DEBOUNCE_DELAY = 500;
export const DEBOUNCE_MAX_WAIT = 5_000;

/**
 * @description Editable clone:
 * - Create a unique clone
 * - Will handle loaded state (dataSource undefined or isLoaded param)
 */
export function useCloneOnce<T>(dataSource: MaybeRef<T>, isLoaded?: MaybeRef<boolean>) {
  // Cast to make TS happy
  // https://github.com/vuejs/core/issues/1324#issuecomment-641150163
  // deepToRaw() ensure that there is no nested “ref” coming from the dataSource inside the new modelValue
  const modelValue = ref(cloneSource()) as Ref<ReturnType<typeof deepToRaw<T>>>;

  // We only want to update from first initialization
  // • we don't want racing issue where a user might loose its changes made before a dataSource update
  // • useCase is: the local copy updates will be the same as what will be persisted serve-side
  const shouldWatchUndefinedDataSource = typeof isLoaded === `undefined` && typeof toValue(dataSource) === `undefined`;
  if (shouldWatchUndefinedDataSource) {
    const stopWatcher = watch(toRef(dataSource), (newValue) => {
      if (typeof newValue === `undefined`) return;
      modelValue.value = cloneSource();
      stopWatcher();
    });
  }
  const shouldWatchIsLoadedRef = typeof isLoaded !== `undefined`;
  if (shouldWatchIsLoadedRef) {
    const stopWatcher = watch(toRef(isLoaded), (newValue) => {
      if (newValue !== true) return;
      modelValue.value = cloneSource();
      stopWatcher();
    });
  }

  function cloneSource() {
    return deepToRaw(dataSource);
  }

  function syncWithSource() {
    modelValue.value = cloneSource();
  }

  return {
    modelValue,
    syncWithSource,
  };
}

interface Options {
  debounceDelay?: number
  debounceMaxWait?: number
}

/**
 * @description Editable clone with debounce utility:
 * - Create a unique clone once is loaded
 * - Never automatically update editable clone.
 *   Doing it might create racing issues between auto-updates and user edits
 * - Will fire ”onEditEnd” on every debounced update
 */
export function useDebouncedCloneOnce<T>(dataSource: MaybeRef<T>, isLoaded?: MaybeRef<boolean>, options?: Options) {
  const { modelValue, syncWithSource } = useCloneOnce(dataSource, isLoaded);
  const { modelValue: debouncedValue, syncWithSource: syncDebouncedWithSource } = useCloneOnce(modelValue, isLoaded);

  const debounceDelay = options?.debounceDelay || DEBOUNCE_DELAY;
  const maxWait = options?.debounceMaxWait || DEBOUNCE_MAX_WAIT;

  const editEnd = createEventHook();

  const debouncedTrigger = useDebounceFn(() => {
    syncDebouncedWithSource();
    editEnd.trigger();
  }, debounceDelay, { maxWait });

  // Make a custom watchDebounced over VueUse one
  // • original one only supports reactive and not ref
  //   https://vueuse.org/shared/watchdebounced/
  // • we also need to prevent some “trigger”
  //   - first initialization
  //   - synching back from dataSource
  // • no need for a “ref”: it is an internal status without reactivity tracking
  let preventDebounceTrigger = typeof isLoaded === `undefined`
    ? typeof toValue(dataSource) === `undefined`
    : !toValue(isLoaded);

  watch(modelValue, () => {
    if (preventDebounceTrigger) {
      preventDebounceTrigger = false;
      return;
    }
    debouncedTrigger();
  }, { deep: true });

  function resetFromSource() {
    preventDebounceTrigger = true;
    syncWithSource();
    syncDebouncedWithSource();
  }

  const debouncedModelValue = computed(() => debouncedValue.value);

  return {
    modelValue,
    debouncedModelValue,
    resetFromSource,
    onEditEnd: editEnd.on,
  };
}

export function deepToRaw<T>(sourceObj: MaybeRef<T>) {
  // use “toValue” instead of “unref” to run getters
  const value = toValue(sourceObj);
  // We need to recursively go through all our object and unwrap all ref
  // • This is the behavior of Klona
  // • By cloning every property, everything will be readable again
  return klona(value) as UnwrapNestedRefs<WritableDeep<T>>;
}
